import { createTheme } from '@mui/material';

declare module '@mui/material' {
    interface TypographyPropsVariantOverrides {
        adornment: true;
        body: true;
        bodyMedium: true;
        bodySmall: true;
        bodyExtraSmall: true;
    }

    interface ButtonPropsVariantOverrides {
        grayText: true;
        green: true;
        purple: true;
    }
}

// Borders
const lightBorder = '#e0e0e0';
const dataBlockBorder = '#dbdbdb';

// General
export const backgroundColor = '#f7f6ff';

// Containers
export const boxShadow = 'rgba(0, 0, 0, 0.15) 0px 2px 4px 0px';

// Text
export const grayText = '#999999';

export default createTheme({
    palette: {
        primary: {
            main: '#4c17ad',
        },
        error: {
            main: '#ff0000',
        },
        border: {
            main: lightBorder,
            dataBlock: dataBlockBorder,
        },
    },
    typography: {
        allVariants: {
            fontFamily: 'Inter',
            letterSpacing: 0,
        },
        h1: {
            fontSize: '2.1875rem',
            fontWeight: 500,
            letterSpacing: 0,
            margin: '0px 0px 0.9375rem',
        },
        h2: {
            fontSize: '1.5625rem',
            fontWeight: 500,
            letterSpacing: 0,
            margin: '0px 0px 0.9375rem',
        },
        h4: {
            fontSize: '1rem',
            fontWeight: 600,
            fontFamily: 'Inter',
            letterSpacing: 0,
            margin: '0px 0px 0.9375rem',
        },
        h5: {
            fontSize: '0.875rem',
            margin: '0px 0px 1.25rem',
            letterSpacing: '0px',
            fontFamily: 'Inter',
            fontWeight: 600,
            lineHeight: '1.375rem',
        },
    },
    components: {
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    height: '100%',
                },
            },
        },
        MuiTypography: {
            variants: [
                {
                    props: { variant: 'adornment' },
                    style: {
                        height: '100%',
                        backgroundColor: '#d6d6d6',
                        color: 'rgb(10, 10, 10)',
                        padding: '15px',
                        marginBottom: '0px',
                        overflow: 'hidden',
                        borderEndEndRadius: '4px',
                        borderStartEndRadius: '4px',
                    },
                },
                {
                    props: { variant: 'body' },
                    style: {
                        display: 'block',
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        lineHeight: '1.625rem',
                        letterSpacing: 0,
                        color: '#5B5B5B',
                        margin: '0px 0px 2.1875rem',
                    },
                },
                {
                    props: { variant: 'bodyMedium' },
                    style: {
                        display: 'block',
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        color: grayText,
                        lineHeight: '1.375rem',
                        letterSpacing: 0,
                        margin: '0px 0px 1.5625rem',
                    },
                },
                {
                    props: { variant: 'bodySmall' },
                    style: {
                        display: 'block',
                        fontSize: '0.75rem',
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        lineHeight: '1.625rem',
                        letterSpacing: 0,
                        marginBottom: '20px',
                    },
                },
                {
                    props: { variant: 'bodyExtraSmall' },
                    style: {
                        display: 'block',
                        fontSize: '0.625rem',
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        lineHeight: '1.625rem',
                        letterSpacing: 0,
                        marginBottom: '20px',
                    },
                },
            ],
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    // padding: '20px !important',
                    // maxWidth: 'none !important',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                    letterSpacing: '0px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    lineHeight: '1.375rem',
                    margin: '0px',
                    padding: '12px 25px',
                },
            },
            variants: [
                {
                    props: { variant: 'grayText' },
                    style: {
                        fontSize: '0.875rem',
                        fontFamily: 'Inter',
                        fontWeight: 600,
                        lineHeight: '1.375rem',
                        color: grayText,
                        '&:hover': {
                            backgroundColor: 'rgba(91, 91, 91, 0.1) !important',
                        },
                    },
                },
                {
                    props: { variant: 'purple' },
                    style: {
                        margin: 0,
                        color: 'white',
                        backgroundColor: '#4c17ad',
                        '&:hover': {
                            backgroundColor: 'rgba(76, 23, 173, 0.8)',
                        },
                        '&:disabled': {
                            backgroundColor: '#bf9dff',
                            color: 'white',
                        },
                        '@media (width <= 850px)': {
                            width: '100%',
                        },
                    },
                },
                {
                    props: { variant: 'green' },
                    style: {
                        margin: 0,
                        color: 'white',
                        backgroundColor: 'rgba(55, 128, 0, 0.9)',
                        '&:hover': {
                            backgroundColor: '#388000 !important',
                        },
                    },
                },
            ],
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fbfcff',
                    fontSize: '0.875rem',
                    margin: '0px',
                    paddingRight: '0px !important',
                    letterSpacing: '0px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    lineHeight: '1.375rem',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: '0.875rem',
                    letterSpacing: '0px',
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    lineHeight: '1.375rem',
                    color: '#0a0a0a',
                },
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    width: '20px !important',
                    height: '20px !important',
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    fontFamily: 'Inter',
                },
            },
        },
    },
});
